import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, StackProps } from '@mui/material';
import dayjs from 'dayjs';

import { DateTimePicker, DateTimePickerProps } from './DateTimePicker';

type DateValue = null | string;

type PickerName = 'end' | 'start';

export type DateRangePickerProps = {
  disabled?: boolean;
  endTime?: DateValue;
  EndTimePickerProps?: Partial<DateTimePickerProps>;
  startTime?: DateValue;
  StartTimePickerProps?: Partial<DateTimePickerProps>;
  updateEndTime: (value: null | string) => void;
  updateStartTime: (value: null | string) => void;
  WrapperProps?: StackProps;
};

export const DateTimeRangePicker: FC<DateRangePickerProps> = ({
  disabled,
  endTime = null,
  EndTimePickerProps: endTimePickerProps,
  startTime = null,
  StartTimePickerProps: startTimePickerProps,
  updateEndTime,
  updateStartTime,
  WrapperProps: wrapperProps,
}) => {
  const { t } = useTranslation();

  // Only one calendar is open at a time. Closing is tied to the picker that asked for it, so that
  // the close of the one being replaced cannot shut the one just opened, whatever order they land in.
  const [openPicker, setOpenPicker] = useState<null | PickerName>(null);
  const closePicker = (picker: PickerName) =>
    setOpenPicker(current => (current === picker ? null : current));

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width={1} {...wrapperProps}>
      <DateTimePicker
        disabled={disabled}
        fullWidth
        label={t('from')}
        maxDateTime={endTime ? dayjs(endTime) : undefined}
        onChange={updateStartTime}
        onClose={() => closePicker('start')}
        onOpen={() => setOpenPicker('start')}
        open={openPicker === 'start'}
        value={startTime}
        {...startTimePickerProps}
      />

      <DateTimePicker
        disabled={disabled}
        fullWidth
        label={t('to')}
        minDateTime={startTime ? dayjs(startTime) : undefined}
        onChange={updateEndTime}
        onClose={() => closePicker('end')}
        onOpen={() => setOpenPicker('end')}
        open={openPicker === 'end'}
        value={endTime}
        {...endTimePickerProps}
      />
    </Stack>
  );
};
